import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { logout, setAuthToken, isTokenValid } from '../utils/auth';
import '../styles/CustomerDashboard.css';

function CustomerDashboard() {
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  const [standaloneVouchers, setStandaloneVouchers] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeView, setActiveView] = useState('programs');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && isTokenValid()) {
      setAuthToken(token);
      setIsLoggedIn(true);
      fetchData();
    } else {
      localStorage.removeItem('token');
      setIsLoggedIn(false);
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch user profile
      const profileResponse = await axios.get('/api/customers/profile');
      setUserProfile(profileResponse.data);

      // Fetch enrolled programs and vouchers
      const programsResponse = await axios.get('/api/customers/programs');
      
      if (programsResponse.data && programsResponse.data.enrolledPrograms) {
        // Sort programs by vendor name
        const sortedPrograms = [...programsResponse.data.enrolledPrograms].sort((a, b) => 
          a.vendor_name.localeCompare(b.vendor_name)
        );
        setEnrolledPrograms(sortedPrograms);
      } else {
        setEnrolledPrograms([]);
      }
      
      if (programsResponse.data && programsResponse.data.standaloneVouchers) {
        const sortedVouchers = [...programsResponse.data.standaloneVouchers].sort((a, b) => 
          a.vendor_name.localeCompare(b.vendor_name)
        );
        setStandaloneVouchers(sortedVouchers);
      } else {
        setStandaloneVouchers([]);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load your data. Please try again later.');
      
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleView = (view) => {
    setActiveView(view);
  };

  const renderEmptyState = () => (
    <div className="cust-dash__empty-state">
      <div className="cust-dash__empty-icon"></div>
      <h3>No {activeView === 'programs' ? 'Loyalty Programs' : 'Vouchers'} Yet</h3>
      <p>Visit local businesses to discover and join their loyalty programs.</p>
      <Link to="/vendors" className="cust-dash__discover-btn">Discover Businesses</Link>
    </div>
  );

  const groupProgramsByVendor = () => {
    const groupedPrograms = {};
    
    enrolledPrograms.forEach(program => {
      if (!groupedPrograms[program.vendor_sub_url]) {
        groupedPrograms[program.vendor_sub_url] = {
          vendor_name: program.vendor_name,
          vendor_sub_url: program.vendor_sub_url,
          programs: []
        };
      }
      groupedPrograms[program.vendor_sub_url].programs.push(program);
    });
    
    return Object.values(groupedPrograms);
  };

  const renderLoyaltyPrograms = () => {
    const vendorGroups = groupProgramsByVendor();
    
    if (vendorGroups.length === 0) {
      return renderEmptyState();
    }
    
    return (
      <div className="cust-dash__vendor-groups">
        {vendorGroups.map((vendorGroup) => (
          <div key={vendorGroup.vendor_sub_url} className="cust-dash__vendor-group">
            <div className="cust-dash__vendor-header">
              <h3>{vendorGroup.vendor_name}</h3>
              <Link 
                to={`/vendor/${vendorGroup.vendor_sub_url}`} 
                className="cust-dash__visit-vendor-btn"
              >
                Visit
              </Link>
            </div>
            
            <div className="cust-dash__programs-grid">
              {vendorGroup.programs.map(program => (
                <div key={program.id} className="cust-dash__program-card">
                  <div className="cust-dash__program-info">
                    <h4>{program.name}</h4>
                    <div className="cust-dash__progress-container">
                      <div className="cust-dash__progress-bar">
                        <div 
                          className="cust-dash__progress-fill" 
                          style={{ width: `${(program.stamps / program.stamps_required) * 100}%` }}
                        ></div>
                      </div>
                      <span className="cust-dash__progress-text">
                        {program.stamps} / {program.stamps_required} stamps
                      </span>
                    </div>
                    <p className="cust-dash__reward-text">
                      {program.description || "Reward"}
                    </p>
                  </div>
                  <Link 
                    to={`/loyalty-card-v3/${program.id}`} 
                    className="cust-dash__view-card-btn"
                  >
                    View Card
                  </Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderVouchers = () => {
    if (standaloneVouchers.length === 0) {
      return renderEmptyState();
    }

    // Group vouchers by vendor
    const groupedVouchers = {};
    standaloneVouchers.forEach(voucher => {
      if (!groupedVouchers[voucher.vendor_sub_url]) {
        groupedVouchers[voucher.vendor_sub_url] = {
          vendor_name: voucher.vendor_name,
          vendor_sub_url: voucher.vendor_sub_url,
          vouchers: []
        };
      }
      groupedVouchers[voucher.vendor_sub_url].vouchers.push(voucher);
    });

    return (
      <div className="cust-dash__vendor-groups">
        {Object.values(groupedVouchers).map(group => (
          <div key={group.vendor_sub_url} className="cust-dash__vendor-group">
            <div className="cust-dash__vendor-header">
              <h3>{group.vendor_name}</h3>
              <Link 
                to={`/vendor/${group.vendor_sub_url}`} 
                className="cust-dash__visit-vendor-btn"
              >
                Visit
              </Link>
            </div>
            
            <div className="cust-dash__vouchers-grid">
              {group.vouchers.map(voucher => (
                <div 
                  key={voucher.id} 
                  className={`cust-dash__voucher-card ${voucher.redeemed_at ? 'cust-dash__voucher-card--redeemed' : ''}`}
                >
                  <div className="cust-dash__voucher-status">
                    {voucher.redeemed_at ? (
                      <span className="cust-dash__status-badge cust-dash__status-badge--redeemed">
                        Redeemed
                      </span>
                    ) : (
                      <span className="cust-dash__status-badge cust-dash__status-badge--available">
                        Available
                      </span>
                    )}
                  </div>
                  <div className="cust-dash__voucher-content">
                    <h4>{voucher.description || `R${voucher.value} Voucher`}</h4>
                    <p className="cust-dash__voucher-date">
                      Created: {new Date(voucher.created_at).toLocaleDateString()}
                    </p>
                    {voucher.redeemed_at && (
                      <p className="cust-dash__voucher-date">
                        Redeemed: {new Date(voucher.redeemed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {!voucher.redeemed_at && (
                    <Link 
                      to={`/vendor/${voucher.vendor_sub_url}`} 
                      className="cust-dash__redeem-btn"
                    >
                      Redeem at Vendor
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="cust-dash">
        <div className="cust-dash__login-required">
          <div className="cust-dash__login-card">
            <div className="cust-dash__login-icon"></div>
            <h2>Login Required</h2>
            <p>Please log in to view your loyalty programs and rewards.</p>
            <div className="cust-dash__login-actions">
              <Link to="/login" className="cust-dash__login-btn">Login</Link>
              <Link to="/register" className="cust-dash__register-btn">Register</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cust-dash">
        <div className="cust-dash__loading">
          <div className="cust-dash__loading-spinner"></div>
          <p>Loading your rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cust-dash">
      <header className="cust-dash__header">
        <div className="cust-dash__header-content">
          <h1>My Rewards</h1>
          {userProfile && (
            <div className="cust-dash__user-info">
              <span className="cust-dash__user-greeting">Hello, {userProfile.full_name.split(' ')[0]}</span>
              <span className="cust-dash__loyalty-number">ID: {userProfile.loyalty_number}</span>
            </div>
          )}
        </div>
        <button className="cust-dash__logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </header>

      {error && (
        <div className="cust-dash__error-message">
          {error}
          <button onClick={fetchData} className="cust-dash__retry-btn">
            Retry
          </button>
        </div>
      )}

      <div className="cust-dash__tabs">
        <button 
          className={`cust-dash__tab-btn ${activeView === 'programs' ? 'cust-dash__tab-btn--active' : ''}`}
          onClick={() => toggleView('programs')}
        >
          Loyalty Programs
          <span className="cust-dash__badge">{enrolledPrograms.length}</span>
        </button>
        <button 
          className={`cust-dash__tab-btn ${activeView === 'vouchers' ? 'cust-dash__tab-btn--active' : ''}`}
          onClick={() => toggleView('vouchers')}
        >
          Vouchers
          <span className="cust-dash__badge">{standaloneVouchers.length}</span>
        </button>
      </div>

      <div className="cust-dash__content">
        {activeView === 'programs' ? renderLoyaltyPrograms() : renderVouchers()}
      </div>
    </div>
  );
}

export default CustomerDashboard;